import { Edge } from 'reactflow';
import { TableData, TableNode, ColumnType, Column } from '../stores/schema-designer-store';

export type DatabaseType = 'postgres' | 'mysql' | 'sqlite';

/**
 * Get SQL type string for a column based on database type
 */
const getSqlType = (type: ColumnType, length?: number, precision?: number, scale?: number, dbType: DatabaseType = 'postgres'): string => {
  switch (type) {
    case 'varchar':
      return `VARCHAR(${length || 255})`;

    case 'text':
      return 'TEXT';

    case 'integer':
      return dbType === 'postgres' ? 'INTEGER' : 'INT';

    case 'bigint':
      return 'BIGINT';

    case 'float':
      return dbType === 'postgres' ? 'REAL' : 'FLOAT';

    case 'double':
      return dbType === 'postgres' ? 'DOUBLE PRECISION' : 'DOUBLE';

    case 'decimal':
      return `DECIMAL(${precision || 10},${scale || 2})`;

    case 'boolean':
      return dbType === 'mysql' ? 'TINYINT(1)' : 'BOOLEAN';

    case 'date':
      return 'DATE';

    case 'datetime':
      return dbType === 'postgres' ? 'TIMESTAMP' : 'DATETIME';

    case 'timestamp':
      return dbType === 'postgres' ? 'TIMESTAMP WITH TIME ZONE' : 'TIMESTAMP';

    case 'time':
      return 'TIME';

    case 'uuid':
      return dbType === 'postgres' ? 'UUID' : 'VARCHAR(36)';

    case 'json':
      return dbType === 'postgres' ? 'JSON' : 'JSON';

    case 'jsonb':
      return dbType === 'postgres' ? 'JSONB' : 'JSON';

    case 'enum':
      return 'VARCHAR(50)'; // Simplified, real enum would need enum values

    case 'array':
      return dbType === 'postgres' ? 'TEXT[]' : 'TEXT'; // Arrays only in PostgreSQL

    default:
      return 'VARCHAR(255)';
  }
};

/**
 * Generate SQL for a single column
 */
const generateColumnSQL = (column: Column, dbType: DatabaseType = 'postgres'): string => {
  const { name, type, length, precision, scale, nullable, primaryKey, unique, autoIncrement, defaultValue, comment } = column;

  let definition = `  ${name} ${getSqlType(type, length, precision, scale, dbType)}`;

  // Primary key
  if (primaryKey) {
    if (dbType === 'mysql' || dbType === 'sqlite') {
      definition += ' PRIMARY KEY';
    }
    if (autoIncrement) {
      if (dbType === 'postgres') {
        definition = `  ${name} SERIAL`;
      } else if (dbType === 'mysql') {
        definition += ' AUTO_INCREMENT';
      } else {
        definition += ' AUTOINCREMENT';
      }
    }
  }

  // Not null
  if (!nullable && !primaryKey) {
    definition += ' NOT NULL';
  }

  // Unique
  if (unique && !primaryKey) {
    definition += ' UNIQUE';
  }

  // Default value
  if (defaultValue !== undefined && defaultValue !== '') {
    // Handle different default value types
    if (defaultValue === 'CURRENT_TIMESTAMP' || defaultValue === 'NOW()') {
      definition += ` DEFAULT ${dbType === 'postgres' ? 'CURRENT_TIMESTAMP' : defaultValue}`;
    } else if (type === 'boolean') {
      definition += ` DEFAULT ${defaultValue}`;
    } else if (['integer', 'bigint', 'float', 'double', 'decimal'].includes(type)) {
      definition += ` DEFAULT ${defaultValue}`;
    } else {
      definition += ` DEFAULT '${defaultValue}'`;
    }
  }

  // Comment (PostgreSQL specific)
  if (comment && dbType === 'postgres') {
    definition += ` -- ${comment}`;
  }

  return definition;
};

/**
 * Generate CREATE TABLE statement for a single table
 */
export const generateTableSQL = (table: TableData, dbType: DatabaseType = 'postgres'): string => {
  const { name, columns, indexes, comment } = table;

  if (!columns.length) {
    return `CREATE TABLE ${name} (\n  id ${dbType === 'postgres' ? 'SERIAL' : dbType === 'mysql' ? 'INT AUTO_INCREMENT' : 'INTEGER'} PRIMARY KEY\n);\n`;
  }

  const columnDefinitions = columns.map((column) => generateColumnSQL(column, dbType));

  // Add primary key constraint for PostgreSQL if using SERIAL
  const hasPrimaryKey = columns.some((column) => column.primaryKey);
  const primaryKeyColumns = columns.filter((col) => col.primaryKey).map((col) => col.name);

  if (hasPrimaryKey && dbType === 'postgres' && primaryKeyColumns.length > 0) {
    const pkCols = columns.filter((col) => col.primaryKey && !col.autoIncrement);
    if (pkCols.length > 0) {
      columnDefinitions.push(`  PRIMARY KEY (${pkCols.map((col) => col.name).join(', ')})`);
    }
  }

  // Add indexes
  if (indexes && indexes.length > 0) {
    indexes.forEach((index) => {
      const indexType = index.unique ? 'UNIQUE INDEX' : 'INDEX';
      columnDefinitions.push(`  ${indexType} ${index.name} (${index.columns.join(', ')})`);
    });
  }

  let sql = `CREATE TABLE ${name} (\n${columnDefinitions.join(',\n')}\n);\n`;

  // Add table comment (PostgreSQL)
  if (comment && dbType === 'postgres') {
    sql += `\nCOMMENT ON TABLE ${name} IS '${comment}';\n`;
  }

  // Add column comments (PostgreSQL)
  if (dbType === 'postgres') {
    const columnsWithComments = columns.filter((col) => col.comment);
    columnsWithComments.forEach((col) => {
      sql += `COMMENT ON COLUMN ${name}.${col.name} IS '${col.comment}';\n`;
    });
  }

  return sql;
};

/**
 * Extract relationship information from edges
 */
const getRelationships = (
  nodes: TableNode[],
  edges: Edge[]
): Array<{ source: string; target: string; sourceTable: string; targetTable: string }> => {
  return edges
    .map((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const targetNode = nodes.find((node) => node.id === edge.target);

      return {
        source: edge.source,
        target: edge.target,
        sourceTable: sourceNode?.data.name || '',
        targetTable: targetNode?.data.name || '',
      };
    })
    .filter((rel) => rel.sourceTable && rel.targetTable);
};

/**
 * Generate foreign key constraints based on relationships
 */
const generateForeignKeySQL = (
  relationships: Array<{ source: string; target: string; sourceTable: string; targetTable: string }>,
  dbType: DatabaseType = 'postgres'
): string => {
  return relationships
    .map((rel) => {
      const columnName = `${rel.targetTable.toLowerCase()}_id`;
      return `ALTER TABLE ${rel.sourceTable} ADD COLUMN ${columnName} ${dbType === 'postgres' ? 'INTEGER' : 'INT'} REFERENCES ${rel.targetTable}(id);\n`;
    })
    .join('');
};

/**
 * Generate complete SQL schema from nodes and edges
 */
export const generateSchemaSQL = (nodes: TableNode[], edges: Edge[], dbType: DatabaseType = 'postgres'): string => {
  if (!nodes.length) {
    return '-- No tables defined yet. Add some tables to generate SQL.';
  }

  // Generate table creation SQL
  const tableSQL = nodes.map((node) => generateTableSQL(node.data, dbType)).join('\n');

  // Generate relationship SQL (foreign keys)
  const relationships = getRelationships(nodes, edges);
  const relationshipSQL = relationships.length > 0 ? '\n-- Foreign Key Constraints\n' + generateForeignKeySQL(relationships, dbType) : '';

  return tableSQL + relationshipSQL;
};

/**
 * Generate CREATE TABLE statements for migration
 */
export const generateMigrationSQL = (
  nodes: TableNode[],
  edges: Edge[],
  dbType: DatabaseType = 'postgres',
  migrationName?: string
): { up: string; down: string } => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const name = migrationName || 'create_schema';

  const up = [
    `-- Migration: ${name}`,
    `-- Created at: ${new Date().toISOString()}`,
    `-- Database: ${dbType.toUpperCase()}`,
    '',
    generateSchemaSQL(nodes, edges, dbType),
  ].join('\n');

  const down = [
    `-- Rollback migration: ${name}`,
    '',
    ...nodes.reverse().map((node) => `DROP TABLE IF EXISTS ${node.data.name} CASCADE;`),
  ].join('\n');

  return { up, down };
};

/**
 * Export schema as JSON
 */
export const exportSchemaAsJSON = (nodes: TableNode[], edges: Edge[]): string => {
  return JSON.stringify(
    {
      version: '1.0',
      tables: nodes.map((node) => ({
        name: node.data.name,
        columns: node.data.columns,
        indexes: node.data.indexes,
        comment: node.data.comment,
      })),
      relationships: getRelationships(nodes, edges),
    },
    null,
    2
  );
};

/**
 * Generate SQL for reverse engineering (from existing database schema)
 */
export const generateReverseEngineerSQL = (dbType: DatabaseType = 'postgres'): string => {
  switch (dbType) {
    case 'postgres':
      return `
SELECT
  table_name,
  column_name,
  data_type,
  character_maximum_length,
  is_nullable,
  column_default
FROM information_schema.columns
WHERE table_schema = 'public'
ORDER BY table_name, ordinal_position;
      `.trim();

    case 'mysql':
      return `
SELECT
  TABLE_NAME as table_name,
  COLUMN_NAME as column_name,
  DATA_TYPE as data_type,
  CHARACTER_MAXIMUM_LENGTH as character_maximum_length,
  IS_NULLABLE as is_nullable,
  COLUMN_DEFAULT as column_default
FROM INFORMATION_SCHEMA.COLUMNS
WHERE TABLE_SCHEMA = DATABASE()
ORDER BY TABLE_NAME, ORDINAL_POSITION;
      `.trim();

    case 'sqlite':
      return `
SELECT
  name as table_name,
  sql
FROM sqlite_master
WHERE type = 'table'
AND name NOT LIKE 'sqlite_%'
ORDER BY name;
      `.trim();

    default:
      return '';
  }
};
